import React, { useEffect, useState } from "react";
import { Box, Button, useTheme, Typography, Stack, Tooltip, TextField, FormControl } from "@mui/material";
import AxionControls from "../../../components/controls/AxionControls";

import messages from "./messages";
import { tokens } from "../../../assets/theme";
import { FormattedMessage, useIntl } from "react-intl";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { updateActiveProductionOrder, createActiveProductionOrder, deleteActiveProductionOrder } from "../../../fetchers/activeProductionOrders";
import { getActiveProductionOrders } from "../../../fetchers/activeProductionOrders";
import { fetchMachines } from "../../../fetchers/machine";
import { fetchProductionItems } from "../../../fetchers/productionItems";
import { fetchTools } from "../../../fetchers/tools";
import { useQuery } from "@tanstack/react-query";
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';

export default function ActivePOUpdateDialog(props) {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { formatMessage } = useIntl();
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [itemsForMachine, setItemsForMachine] = useState([]);
    const [localProductionOrder, setLocalProductionOrder] = useState({});
    const [buttonDisabled, setButtonDisabled] = React.useState(false);
    const [selectedItemCycleTime, setSelectedItemCycleTime] = useState(null);
    const [selectedItemCavities, setSelectedItemCavities] = useState(null);
    const [tools, setTools] = useState([]);

    const [confirmationTitle, setConfirmationTitle] = useState("");
    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [openConfirmationDelete, setOpenConfirmationDelete] = useState(false);
    const [ongoingPO, setOngoingPO] = useState(null);

    const machineId = props.machineId;
    const [action, setAction] = useState(props.action); // "start" or "report" or "stop"
    let localRemaining = 0;


    const activeProductionOrdersQuery = useQuery(
        ["activeProductionOrders", companyKey],
        () => getActiveProductionOrders(companyKey),
        {}
    );

    const machineQuery = useQuery(
        ["machines", companyKey],
        () => fetchMachines(companyKey),
        {}
    );

    const productionItemsQuery = useQuery(
        ["productionItems", companyKey],
        () => fetchProductionItems(companyKey), {
        // staleTime: 60000,
        // refetchInterval: 120000,
    });

    const toolsQuery = useQuery(
        ["tools", companyKey],
        () => fetchTools(companyKey),
        {
            staleTime: Infinity
        }
    );

    const newOrderTemplate = {
        erpId: "x",
        companyKey: companyKey,
        machineId: machineId,
        activeProductionOrderNumber: null,
        activeTool: null,
        idealCycleTime: null,
        plannedQuantity: 0,
        reportedQuantity: 0,
        remainingQuantity: 0,
        activeItemId: "#NA",
        cavities: null,
        status: "new"
    };


    useEffect(() => {
        setTools(
            toolsQuery.data?.map((tool) => ({
                key: tool.id,
                id: tool.id,
                title: tool.name
            })) || []
        );
    }, [toolsQuery.data]);


    useEffect(() => {

        setItemsForMachine(productionItemsQuery.data?.map((item, index) => ({
            key: item.itemNo, id: item.itemNo, title: `${item.itemNo} - ${item.name}`
        })));

        let currentPO = getCurrentPO(machineId);
        setOngoingPO(currentPO);

        switch (action) {
            case "start":
                // setConfirmationTitle(formatMessage(messages.startActiveProductionOrderTitle));
                // setConfirmationMessage(formatMessage(messages.startActiveProductionOrderMessage));
                setLocalProductionOrder(newOrderTemplate);
                break;
            case "report":
                // setConfirmationTitle(formatMessage(messages.reportActiveProductionOrderTitle));
                // setConfirmationMessage(formatMessage(messages.reportActiveProductionOrderMessage));
                // if (currentPO.activeProductionOrderNumber === null ){
                //     setLocalProductionOrder(newOrderTemplate);
                //     setAction("start");
                //     break;
                // }
                setLocalProductionOrder(currentPO);
                showIdealCycleTimeForItem(currentPO.activeItemId);
                break;
            case "stop":
                // setConfirmationTitle(formatMessage(messages.stopActiveProductionOrderTitle));
                // setConfirmationMessage(formatMessage(messages.stopActiveProductionOrderMessage));
                setLocalProductionOrder(currentPO);
                showIdealCycleTimeForItem(currentPO.activeItemId);
                break;
            default:
                setConfirmationTitle("");
                setConfirmationMessage("");
        }
    }, [productionItemsQuery.data, activeProductionOrdersQuery.data]);

    // get existing PO or an empty template for the machine
    function getCurrentPO(machineId) {
        let po = activeProductionOrdersQuery.data?.find((order) => order.machineId.toString() === machineId.toString());
        if (!po) {
            po = {
                erpId: "x",
                companyKey: companyKey,
                machineId: machineId,
                activeProductionOrderNumber: null,
                activeTool: null,
                idealCycleTime: null,
                plannedQuantity: 0,
                reportedQuantity: 0,
                remainingQuantity: 0,
                activeItemId: "#NA",
                cavities: null,
                status: "new"
            };
        } else {
            po = {
                ...po,
                status: "update"
            }
        }
        return po;
    }

    const setShowBackdrop = (show) => {
        if (props.setShowBackdrop) {
            props.setShowBackdrop(show);
        }
    }

    const handleRefreshData = () => {
        if (props.handleRefreshData) {
            props.handleRefreshData();
        }
    }

    function getMachineNameById(id) {
        const machine = machineQuery.data?.find((machine) => machine.id === id);
        return machine ? machine.machineName : "";
    }

    function getItemDescByItemNo(itemNo) {
        const item = productionItemsQuery.data?.find((item) => item.itemNo === itemNo);
        return item ? `${item.itemNo} - ${item.name}` : "";
    }

    function calcRemainingQuantity(planned, reported) {
        const plannedNum = parseInt(planned);
        const reportedNum = parseInt(reported);
        if (isNaN(plannedNum) || isNaN(reportedNum)) {
            return "";
        }
        const remaining = plannedNum - reportedNum;
        // if (remaining < 0) {
        //     return 0;
        // }
        setLocalProductionOrder((prev) => ({
            ...prev,
            remainingQuantity: remaining,
        }));
        localRemaining = remaining;
        //return remaining;
    }

    const handleActiveProductionOrderChange = (event) => {
        const { name, value } = event.target;
        setLocalProductionOrder((prev) => ({
            ...prev,
            [name]: value,
        }));

    }

    const handlePlannedQtyChange = (event) => {
        const { name, value } = event.target;
        setLocalProductionOrder((prev) => ({
            ...prev,
            plannedQuantity: value,
        }));
        calcRemainingQuantity(value, localProductionOrder.reportedQuantity)
    }

    const handleReportedQtyChange = (event) => {
        const { name, value } = event.target;
        setLocalProductionOrder((prev) => ({
            ...prev,
            reportedQuantity: value,
        }));
        calcRemainingQuantity(localProductionOrder.plannedQuantity, value)
    }


    const handleActiveProductionOrderChangeSelect = (event, newValue, name) => {
        setLocalProductionOrder((prev) => ({
            ...prev,
            [name]: newValue?.id ?? null,
        }));
    }

    const showIdealCycleTimeForItem = (itemNo) => {
        const item = productionItemsQuery.data?.find((item) => item.itemNo === itemNo);
        if (item) {
            setSelectedItemCycleTime(item.idealCycleTime);
            setSelectedItemCavities(item.cavities);

            if (localProductionOrder.idealCycleTime === null) {
                setLocalProductionOrder((prev) => ({
                    ...prev,
                    idealCycleTime: item.idealCycleTime,
                }));
            }
            if (localProductionOrder.cavities === null) {
                setLocalProductionOrder((prev) => ({
                    ...prev,
                    cavities: item.cavities,
                }));
            }

        } else {
            setSelectedItemCycleTime(null);
            setSelectedItemCavities(null);
        }
    }

    const handleUpdatePO = () => {
        setButtonDisabled(true);
        setShowBackdrop(true);
        if (localProductionOrder.activeProductionOrderNumber === ongoingPO?.activeProductionOrderNumber) {
            updateActivePO();
        } else if (ongoingPO !== null && ongoingPO.activeProductionOrderNumber !== undefined && ongoingPO.activeProductionOrderNumber !== null) {
            updateActivePO();
        } else if (ongoingPO === null || ongoingPO.activeProductionOrderNumber === undefined || ongoingPO.activeProductionOrderNumber === null) {
            CreateActivePO();
        } else {
            console.log("Unknown action type for active production order: ", ongoingPO);
            pushErrorMessage("Unknown action type for active production order: " + localProductionOrder.status);
            setButtonDisabled(false);
            setShowBackdrop(false);
        }
    }

    const updateActivePO = () => {
        let order = {
            ...localProductionOrder,
            companyKey: companyKey.toString(),
            activeTool: localProductionOrder.activeTool?.toString() || ""
        };

        updateActiveProductionOrder(order).then((result) => {

            setShowBackdrop(false);
            setButtonDisabled(false);
            props.setOpenPopup(false);

            pushNotification(formatMessage(messages.orderUpdatedMessage));

            handleRefreshData();
            if (props.invalidateMachineInfo){
                props.invalidateMachineInfo();
            }
        }).catch((error) => {
            console.log("Could not update active production order", error);
            pushErrorMessage("Could not update active production order: " + error?.message);
            setShowBackdrop(false);
            setButtonDisabled(false);
            props.setOpenPopup(false);
        });
    }

    const CreateActivePO = () => {

        let order = {
            erpId: "x",
            companyKey: companyKey.toString(),
            machineId: localProductionOrder.machineId,
            activeProductionOrderNumber: localProductionOrder.activeProductionOrderNumber,
            activeTool: localProductionOrder.activeTool?.toString() || "",
            idealCycleTime: localProductionOrder.idealCycleTime,
            plannedQuantity: localProductionOrder.plannedQuantity,
            reportedQuantity: localProductionOrder.reportedQuantity,
            remainingQuantity: localProductionOrder.remainingQuantity,
            activeItemId: localProductionOrder.activeItemId,
            cavities: localProductionOrder.cavities,
        }

        //Validate required fields
        if (!order.erpId || !order.companyKey || !order.activeProductionOrderNumber || !order.activeItemId || !order.machineId) {

            let errorFields = [];
            if (!order.activeProductionOrderNumber) errorFields.push(formatMessage(messages.activeProductionOrderNumberLabel));
            if (!order.activeItemId) errorFields.push(formatMessage(messages.activeItemLabel));
            if (!order.erpId) errorFields.push(formatMessage(messages.erpIdLabel));
            if (!order.companyKey) errorFields.push(formatMessage(messages.companyKeyLabel));
            if (!order.machineId) errorFields.push(formatMessage(messages.machineIdLabel));

            pushErrorMessage(formatMessage(messages.requiredFieldsMessage) + errorFields.join(", ") + ".");

            setShowBackdrop(false);
            setButtonDisabled(false);
            return;
        }


        createActiveProductionOrder(order).then((result) => {
            setShowBackdrop(false);
            setButtonDisabled(false);
            props.setOpenPopup(false);

            pushNotification(formatMessage(messages.orderCreatedMessage));

            handleRefreshData();
            if (props.invalidateMachineInfo){
                props.invalidateMachineInfo();
            }
        }).catch((error) => {
            console.log("Could not create active production order", error);
            pushErrorMessage("Could not create active production order: " + error?.message);
            setShowBackdrop(false);
            setButtonDisabled(false);
            props.setOpenPopup(false);
        });
    }


    const handleDeletePOClick = () => {
        setOpenConfirmationDelete(true);
    }

    const handleDeletePOOkClick = () => {
        setOpenConfirmationDelete(false);
        handleDeletePO(machineId);
    }

    const handleDeletePOCancelClick = () => {
        setOpenConfirmationDelete(false);
    }

    const handleDeletePO = (machineId) => {
        setButtonDisabled(true);
        setShowBackdrop(true);

        deleteActiveProductionOrder(machineId).then((result) => {

            setShowBackdrop(false);
            setButtonDisabled(false);
            props.setOpenPopup(false);

            pushNotification(formatMessage(messages.orderDeletedMessage));

            setOngoingPO(null);
            //setLocalProductionOrder(null);
            

            handleRefreshData();

            if (props.invalidateMachineInfo){
                props.invalidateMachineInfo();
            }
        }).catch((error) => {
            console.log("Could not delete active production order", error);
            pushErrorMessage("Could not delete active production order: " + error?.message);
            setShowBackdrop(false);
            setButtonDisabled(false);
        });
    }


    if (activeProductionOrdersQuery === undefined || activeProductionOrdersQuery.data === undefined || activeProductionOrdersQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    };
    if (machineQuery === undefined || machineQuery.data === undefined || machineQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    };
    if (productionItemsQuery === undefined || productionItemsQuery.data === undefined || productionItemsQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    };
    if (toolsQuery === undefined || toolsQuery.data === undefined || toolsQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    };

    // Debug logs----------------------------------------------------
    // console.log("[Debug] localproductionOrder: ", localProductionOrder);
    // console.log("[Debug] productionItemsQuery: ", productionItemsQuery?.data?.length);
    // console.log("[Debug] itemsForMachine: ", itemsForMachine);
    // console.log("[Debug] activeItemId: ", localProductionOrder.activeItemId);
    // console.log("[Debug] LocalProductionOrder: ", localProductionOrder);



    return (
        <>
            {(ongoingPO !== null && ongoingPO.activeProductionOrderNumber !== undefined && ongoingPO.activeProductionOrderNumber !== null && action === "start") &&      
                <Box display="flex" justifyContent="center" mb={2} sx={{
                    //display: "grid",
                    //gridTemplateColumns: "repeat(1, 1fr)",
                    gap: 1,
                    padding: "15px",
                    marginTop: "16px",
                    border: "1pt solid #ffffff"
                }}>
                    <FormattedMessage {...messages.activeProductionOrderRestartMessage} /></Box>
            }


            <Stack direction="row" alignItems="flex-start" spacing={10} mb={2}>
                <Box>
                    <Stack direction="column" alignItems="flex-start" spacing={1} mb={2}>
                        <Box sx={{ paddingTop: "10px", paddingLeft: "2px" }}>
                            <TextField 
                                name="activeProductionOrderNumber"
                                label={formatMessage(messages.activeProductionOrderNumberLabel)}
                                value={localProductionOrder?.activeProductionOrderNumber}
                                onChange={handleActiveProductionOrderChange}
                                disabled={action === "report" || action === "stop" ? true : false}
                            />
                        </Box>

                        <FormControl >
                            <AxionControls.AxionAutocomplete
                                name="activeItemId"
                                label={formatMessage(messages.activeItemLabel)}
                                disabled={action === "report" || action === "stop" ? true : false}
                                multiple={false}
                                width="400px"
                                backgroundColor={colors.primary[400]}
                                borderColor={colors.primary[400]}
                                color={colors.primary[100]}
                                curtainBgColor={colors.gray[800]}
                                curtainTextColor={"#fff"}
                                sx={{ mt: "7px", mb: 0, width: "400px" }}
                                //defaultValue={getItemDescByItemNo(localProductionOrder.activeItemId)}
                                value={itemsForMachine?.find((item) => String(item.id) === String(localProductionOrder?.activeItemId)) ?? null}
                                options={itemsForMachine}
                                //onChange={handleActiveProductionOrderChangeSelect}
                                onChange={(event, newValue) => {
                                    handleActiveProductionOrderChangeSelect(event, newValue, "activeItemId");
                                    showIdealCycleTimeForItem(newValue?.id ?? null);
                                }}
                            />
                        </FormControl>
                        <FormControl >
                            <AxionControls.AxionAutocomplete
                                name="activeTool"
                                label={formatMessage(messages.activeToolLabel)}
                                disabled={action === "report" || action === "stop" ? true : false}
                                multiple={false}
                                width="400px"
                                backgroundColor={colors.primary[400]}
                                borderColor={colors.primary[400]}
                                color={colors.primary[100]}
                                curtainBgColor={colors.gray[800]}
                                curtainTextColor={"#fff"}
                                sx={{ mt: "7px", mb: 0, width: "400px" }}
                                //defaultValue={getItemDescByItemNo(localProductionOrder.activeItemId)}
                                value={tools.find((tool) => String(tool.id) === String(localProductionOrder?.activeTool)) ?? null}
                                options={tools}
                                //onChange={handleActiveProductionOrderChangeSelect}
                                onChange={(event, newValue) => {
                                    handleActiveProductionOrderChangeSelect(event, newValue, "activeTool");

                                }}
                            />
                        </FormControl>
                        <Stack sx={{ paddingTop: "10px", paddingLeft: "2px" }} direction="row" spacing={2} alignItems="center">
                            <TextField
                                name="idealCycleTime"
                                label={formatMessage(messages.idealCycleTimeLabel)}
                                value={localProductionOrder?.idealCycleTime}
                                onChange={handleActiveProductionOrderChange}
                                disabled={action === "stop" ? true : false}
                                InputLabelProps={{
                                    shrink:
                                        localProductionOrder.idealCycleTime !== null &&
                                        localProductionOrder.idealCycleTime !== undefined &&
                                        localProductionOrder.idealCycleTime !== "",
                                }}
                            />
                            <Box>
                                {selectedItemCycleTime?.toString() !== localProductionOrder?.idealCycleTime?.toString() &&
                                    <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.cycleTimeWarningLabel} /> {selectedItemCycleTime}</Typography>}>
                                        <AnnouncementOutlinedIcon fontSize="medium" />
                                    </Tooltip>
                                }
                            </Box>
                        </Stack>


                        <Stack sx={{ paddingTop: "10px", paddingLeft: "2px" }} direction="row" spacing={2} alignItems="center">
                            <TextField
                                name="cavities"
                                label={formatMessage(messages.cavitiesLabel)}
                                value={localProductionOrder?.cavities}
                                onChange={handleActiveProductionOrderChange}
                                disabled={action === "stop" ? true : false}
                                InputLabelProps={{
                                    shrink:
                                        localProductionOrder.cavities !== null &&
                                        localProductionOrder.cavities !== undefined &&
                                        localProductionOrder.cavities !== "",
                                }}
                            />
                            <Box>
                                {selectedItemCavities?.toString() !== localProductionOrder?.cavities?.toString() &&
                                    <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.cavityWarningLabel} /> {selectedItemCavities}</Typography>}>
                                        <AnnouncementOutlinedIcon fontSize="medium" />
                                    </Tooltip>
                                }
                            </Box>
                        </Stack>

                    </Stack>

                </Box>
                <Box>
                    <Stack direction="column" alignItems="flex-start" spacing={1} mb={2}>
                        <Box sx={{ paddingTop: "10px" }}>
                            <TextField
                                name="plannedQuantity"
                                label={formatMessage(messages.plannedQuantityLabel)}
                                value={localProductionOrder?.plannedQuantity}
                                onChange={handlePlannedQtyChange}
                                disabled={action === "stop" ? true : false}
                            />
                        </Box>
                        <Box sx={{ paddingTop: "10px" }}>
                            <TextField
                                name="reportedQuantity"
                                label={formatMessage(messages.reportedQuantityLabel)}
                                value={localProductionOrder?.reportedQuantity}
                                onChange={handleReportedQtyChange}
                                disabled={action === "stop" ? true : false}
                            />
                        </Box>
                        <Box sx={{ paddingTop: "10px" }}>
                            <TextField
                                name="remainingQuantity"
                                label={formatMessage(messages.remainingQuantityLabel)}
                                value={localProductionOrder?.remainingQuantity}
                                onChange={handleActiveProductionOrderChange}
                                disabled={true}
                                InputLabelProps={{
                                    shrink:
                                        localProductionOrder.remainingQuantity !== null &&
                                        localProductionOrder.remainingQuantity !== undefined &&
                                        localProductionOrder.remainingQuantity !== "",
                                }}
                            />
                        </Box>
                    </Stack>
                </Box>
            </Stack>

            {/* Move buttons to the far right */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                    variant="contained"
                    sx={{
                        marginTop: 2,
                        marginLeft: "auto",
                        display: "block",
                        width: "140px",
                        "&:hover": {
                            backgroundColor: colors.redAccent[700],
                        },
                        backgroundColor: colors.redAccent[500],
                        color: "#ffffff",
                    }}
                    disabled={localProductionOrder?.status === "new" ? true : buttonDisabled}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleDeletePOClick();
                    }}
                >
                    {/* {formatMessage(messages.updPplBtnUpdateOrder)} */}
                    {formatMessage(messages.buttonDeleteOrderLabel)}
                </Button>

                <Button
                    variant="contained"
                    sx={{
                        marginTop: 2,
                        marginLeft: "auto",
                        display: "block",
                        width: "140px",

                    }}

                    disabled={ ( (action === "report" && localProductionOrder?.activeProductionOrderNumber === null) || action === "stop") ? true : buttonDisabled}
                    onClick={handleUpdatePO}
                >
                    {/* {formatMessage(messages.updPplBtnUpdateOrder)} */}
                    {localProductionOrder?.status === "update" ? formatMessage(messages.buttonUpdateOrderLabel) : formatMessage(messages.buttonCreateOrderLabel)}
                </Button>
            </Stack>

            <AxionControls.AxionConfirmation
                open={openConfirmationDelete}
                title={formatMessage(messages.deleteActiveProductionOrderTitle)}
                message={formatMessage(messages.deleteActiveProductionOrderMessage)}
                onConfirm={handleDeletePOOkClick}
                onCancel={handleDeletePOCancelClick}
                cancelText={formatMessage(messages.no)}
                confirmText={formatMessage(messages.yes)}
            // cancelText={formatMessage(messages.no)}
            // confirmText={formatMessage(messages.yes)}
            />
        </>
    );
}